import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";
import { FiPlay, FiClock, FiAlertTriangle, FiImage, FiType, FiVolume2 } from "react-icons/fi";

const specialCharacters = {
  es: ["á", "é", "í", "ó", "ú", "ñ", "ü", "¿", "¡"],
  fr: ["é", "è", "ê", "ë", "à", "â", "ô", "û", "ù", "ï", "ç"],
  de: ["ä", "ö", "ü", "ß"],
  it: ["à", "è", "é", "ì", "ò", "ù"],
  pt: ["á", "â", "ã", "à", "ç", "é", "ê", "í", "ó", "ô", "õ", "ú"],
  nl: ["ij", "á", "é", "í", "ó", "ú"],
};

const WordTest = ({ wordList, singleWordMode = false }) => {
  const [words, setWords] = useState(wordList?.words || []);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [userAnswer, setUserAnswer] = useState("");
  const [showResult, setShowResult] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [wrongAnswers, setWrongAnswers] = useState([]);
  const [earnedCoins, setEarnedCoins] = useState(0);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [showWrongAnswersSummary, setShowWrongAnswersSummary] = useState(false);
  const [correctAnswers, setCorrectAnswers] = useState([]);
  const [retryMode, setRetryMode] = useState(false);
  const inputRef = useRef(null);

  // Hint toggles — user can turn each on/off as they test
  const [showAudio, setShowAudio] = useState(true);
  const [showSentence, setShowSentence] = useState(true);
  const [showImage, setShowImage] = useState(false);

  // Is the test language the same as the base/English language?
  const isBaseLanguage = wordList?.languageName === "English";

  // Do any words have visual data?
  const hasImages = words.some((w) => w.imageUrl);
  const hasSentences = words.some((w) => w.sentence);

  if (!wordList) {
    return (
      <div className="text-center py-8 bg-gray-50 rounded-lg">
        <p className="text-gray-500 text-lg mb-4">
          No word list found. Please create a word list first.
        </p>
        <p className="text-gray-400 text-sm">
          You'll need to create a word list before you can take tests.
        </p>
      </div>
    );
  }

  const currentWord = words[currentWordIndex];
  const languageCode = wordList.language
    ? wordList.language.toLowerCase()
    : wordList.languageName
      ? wordList.languageName.slice(0, 2).toLowerCase()
      : "en";

  useEffect(() => {
    setWords(wordList.words || []);

    if (currentWordIndex > 0 && !showWrongAnswersSummary) {
      resetFullTest();
    }
  }, [wordList]);

  useEffect(() => {
    if (!showResult && inputRef.current) {
      inputRef.current.focus();
    }
  }, [currentWordIndex, showResult]);

  const handleSpecialCharacter = (char) => {
    setUserAnswer((prevAnswer) => prevAnswer + char);
    inputRef.current?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (hasSubmitted) return;

    const correct =
      userAnswer.toLowerCase().trim() ===
      currentWord.word_text.toLowerCase().trim();
    setIsCorrect(correct);
    setShowResult(true);
    setHasSubmitted(true);

    if (correct) {
      setCorrectAnswers((prev) => [...prev, currentWord]);

      if (!singleWordMode) {
        try {
          const response = await fetch("/api/v1/user_rewards/add_coin", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-CSRF-Token": document.querySelector('[name="csrf-token"]')
                ?.content,
            },
          });

          if (response.ok) {
            setEarnedCoins((prev) => prev + 1);
          }
        } catch (error) {
          console.error("Error adding coin:", error);
        }
      } else {
        setEarnedCoins((prev) => prev + 1);
      }
    } else {
      setWrongAnswers((prev) => [
        ...prev,
        {
          word: currentWord,
          userAnswer: userAnswer,
        },
      ]);
    }
  };

  const nextWord = () => {
    if (singleWordMode) {
      resetFullTest();
      return;
    }

    const isLastWord = currentWordIndex === words.length - 1;

    if (isLastWord) {
      setShowWrongAnswersSummary(true);
    } else {
      setCurrentWordIndex((prevIndex) => (prevIndex + 1) % words.length);
      setUserAnswer("");
      setShowResult(false);
      setHasSubmitted(false);
    }
  };

  const playAudio = (audioUrl, rate = 1) => {
    if (!audioUrl) return;
    const audio = new Audio(audioUrl);
    audio.playbackRate = rate;
    audio.play();
  };

  const resetFullTest = () => {
    setWords(wordList.words || []);
    setCurrentWordIndex(0);
    setUserAnswer("");
    setShowResult(false);
    setIsCorrect(false);
    setWrongAnswers([]);
    setCorrectAnswers([]);
    setEarnedCoins(0);
    setHasSubmitted(false);
    setShowWrongAnswersSummary(false);
    setRetryMode(false);
  };

  const retryWrongAnswers = () => {
    setWords(wrongAnswers.map((wa) => wa.word));
    setCurrentWordIndex(0);
    setUserAnswer("");
    setShowResult(false);
    setIsCorrect(false);
    setWrongAnswers([]);
    setHasSubmitted(false);
    setShowWrongAnswersSummary(false);
    setRetryMode(true);
  };

  // Blanked sentence (replace the answer word with underscores)
  const blankedSentence = (word) => {
    if (!word.sentence) return null;
    return word.sentence.replace(
      new RegExp(word.word_text, "gi"),
      "______",
    );
  };

  if (!words.length || !currentWord) {
    return (
      <div className="space-y-6">
        <div className="bg-blue-100 p-4 rounded-lg">
          <p className="text-lg font-semibold">
            Language:{" "}
            <span className="text-blue-600">{wordList.languageName}</span>
          </p>
        </div>
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <FiAlertTriangle className="h-12 w-12 mx-auto text-gray-400 mb-3" />
          <p className="text-gray-500">
            No words available for testing. Please add words in the Edit tab
            first.
          </p>
        </div>
      </div>
    );
  }

  // Summary screen
  if (showWrongAnswersSummary || (singleWordMode && showResult)) {
    if (singleWordMode) {
      return (
        <div className="space-y-6">
          <div className="bg-blue-100 p-4 rounded-lg">
            <h2 className="text-xl font-bold mb-4">Test Result</h2>

            {isCorrect ? (
              <div className="bg-green-100 p-4 rounded-lg">
                <p className="text-lg text-green-700 font-bold">Correct!</p>
                <p className="mt-2">
                  You correctly spelled{" "}
                  <span className="font-bold">{currentWord.word_text}</span>
                </p>
              </div>
            ) : (
              <div className="bg-red-100 p-4 rounded-lg">
                <p className="text-lg text-red-700 font-bold">Oops!</p>
                <p className="mt-2">
                  The correct spelling is{" "}
                  <span className="font-bold">{currentWord.word_text}</span>
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  You entered:{" "}
                  <span className="text-red-600">{userAnswer}</span>
                </p>
              </div>
            )}

            <div className="mt-4">
              <p className="font-medium">Sentence: </p>
              <p className="text-gray-700 italic">{currentWord.sentence}</p>

              <button
                onClick={() => playAudio(currentWord.audioWordUrl)}
                className="mt-4 px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 inline-flex items-center"
              >
                <FiPlay className="mr-1" /> Listen to Word
              </button>
              <button
                onClick={() => playAudio(currentWord.audioSentenceUrl)}
                className="mt-4 ml-2 px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 inline-flex items-center"
              >
                <FiPlay className="mr-1" /> Listen to Sentence
              </button>
            </div>

            <div className="mt-6">
              <button
                onClick={resetFullTest}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="bg-blue-100 p-4 rounded-lg">
          <h2 className="text-xl font-bold mb-4">Test Complete!</h2>
          <p className="text-lg">
            You got {correctAnswers.length} out of {retryMode ? wrongAnswers.length + correctAnswers.length : words.length}{" "}
            words correct!
          </p>

          <div className="mt-4">
            <h3 className="text-lg font-semibold mb-2">Words You Got Right:</h3>
            <div className="space-y-2">
              {correctAnswers.map((correct, index) => (
                <div
                  key={index}
                  className="bg-white p-3 rounded-lg shadow border-l-4 border-green-500"
                >
                  <div className="flex items-center space-x-2">
                    <p className="font-medium text-green-600">
                      {correct.word_text}
                    </p>
                    {correct.english_text && correct.english_text !== correct.word_text && (
                      <span className="text-sm text-gray-400">({correct.english_text})</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Context: {correct.sentence}
                  </p>
                  {correct.audioWordUrl && (
                    <button
                      onClick={() => playAudio(correct.audioWordUrl)}
                      className="mt-2 px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 inline-flex items-center"
                    >
                      <FiPlay className="mr-1" /> Listen
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {wrongAnswers.length > 0 && (
            <div className="mt-4">
              <h3 className="text-lg font-semibold mb-2">Words to Review:</h3>
              <div className="space-y-2">
                {wrongAnswers.map((wrong, index) => (
                  <div key={index} className="bg-white p-3 rounded-lg shadow">
                    <div className="flex items-center space-x-2">
                      <p className="font-medium">
                        Correct:{" "}
                        <span className="text-green-600">
                          {wrong.word.word_text}
                        </span>
                      </p>
                      {wrong.word.english_text && wrong.word.english_text !== wrong.word.word_text && (
                        <span className="text-sm text-gray-400">({wrong.word.english_text})</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">
                      Your answer:{" "}
                      <span className="text-red-600">{wrong.userAnswer}</span>
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      Context: {wrong.word.sentence}
                    </p>
                    {wrong.word.audioWordUrl && (
                      <button
                        onClick={() => playAudio(wrong.word.audioWordUrl)}
                        className="mt-2 px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 inline-flex items-center"
                      >
                        <FiPlay className="mr-1" /> Listen Again
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 space-x-4">
            {wrongAnswers.length > 0 && (
              <button
                onClick={retryWrongAnswers}
                className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600"
              >
                Retry Incorrect Words
              </button>
            )}
            <button
              onClick={resetFullTest}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
            >
              Start New Test
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Active test
  return (
    <div className="space-y-6">
      {/* Header with progress + hint toggles */}
      <div className="bg-blue-100 p-4 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <div>
            <p className="text-lg font-semibold">
              Language:{" "}
              <span className="text-blue-600">{wordList.languageName}</span>
            </p>
            {!singleWordMode && (
              <p className="text-lg font-semibold">
                Word:{" "}
                <span className="text-blue-600">
                  {currentWordIndex + 1} / {words.length}
                </span>
              </p>
            )}
          </div>
        </div>

        {/* Hint toggles */}
        <div className="flex flex-wrap gap-2">
          <span className="text-xs text-gray-500 self-center mr-1">Hints:</span>
          <button
            type="button"
            onClick={() => setShowAudio(!showAudio)}
            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium transition ${
              showAudio
                ? "bg-blue-600 text-white"
                : "bg-white/60 text-gray-500 hover:bg-white"
            }`}
          >
            <FiVolume2 className="mr-1" size={12} />
            Audio
          </button>
          {hasSentences && (
            <button
              type="button"
              onClick={() => setShowSentence(!showSentence)}
              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium transition ${
                showSentence
                  ? "bg-blue-600 text-white"
                  : "bg-white/60 text-gray-500 hover:bg-white"
              }`}
            >
              <FiType className="mr-1" size={12} />
              Sentence
            </button>
          )}
          {hasImages && (
            <button
              type="button"
              onClick={() => setShowImage(!showImage)}
              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium transition ${
                showImage
                  ? "bg-blue-600 text-white"
                  : "bg-white/60 text-gray-500 hover:bg-white"
              }`}
            >
              <FiImage className="mr-1" size={12} />
              Image
            </button>
          )}
        </div>
      </div>

      {/* Hints area */}
      <div className="space-y-4">
        {/* Image hint */}
        {showImage && currentWord.imageUrl && (
          <div className="flex justify-center">
            <div className="w-40 h-40 rounded-xl overflow-hidden shadow-md bg-gray-100">
              <img
                src={currentWord.imageUrl}
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        )}

        {/* English text hint — only for non-base-language tests */}
        {showImage && !isBaseLanguage && currentWord.english_text && currentWord.english_text !== currentWord.word_text && (
          <p className="text-center text-lg font-semibold text-indigo-600">
            {currentWord.english_text}
          </p>
        )}

        {/* Audio buttons */}
        {showAudio && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => playAudio(currentWord.audioWordUrl)}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 inline-flex items-center"
            >
              <FiPlay className="mr-1" /> Say Word
            </button>
            <button
              onClick={() => playAudio(currentWord.audioWordUrl, 0.75)}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 inline-flex items-center"
            >
              <FiClock className="mr-1" /> Slow
            </button>
            {currentWord.audioSentenceUrl && (
              <>
                <button
                  onClick={() => playAudio(currentWord.audioSentenceUrl)}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 inline-flex items-center"
                >
                  <FiPlay className="mr-1" /> Sentence
                </button>
                <button
                  onClick={() => playAudio(currentWord.audioSentenceUrl, 0.5)}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 inline-flex items-center"
                >
                  <FiClock className="mr-1" /> Slow
                </button>
              </>
            )}
          </div>
        )}

        {/* Sentence hint */}
        {showSentence && currentWord.sentence && (
          <p className="text-gray-600 italic">
            {blankedSentence(currentWord)}
          </p>
        )}
      </div>

      {/* Answer input */}
      <form onSubmit={handleSubmit} className="flex flex-col space-y-2">
        <input
          ref={inputRef}
          type="text"
          value={userAnswer}
          onChange={(e) => setUserAnswer(e.target.value)}
          placeholder={`Enter the ${wordList.languageName} word`}
          disabled={hasSubmitted}
          className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
          autoComplete="off"
          autoCapitalize="off"
        />
        {specialCharacters[languageCode] && (
          <div className="flex flex-wrap gap-2 mt-2">
            {specialCharacters[languageCode].map((char, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleSpecialCharacter(char)}
                disabled={hasSubmitted}
                className="px-3 py-1 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {char}
              </button>
            ))}
          </div>
        )}
        <button
          type="submit"
          disabled={hasSubmitted}
          className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Submit Answer
        </button>
      </form>

      {/* Result */}
      {showResult && (
        <div
          className={`p-4 rounded-lg ${isCorrect ? "bg-green-100" : "bg-red-100"}`}
        >
          <p
            className={`text-lg font-semibold ${isCorrect ? "text-green-600" : "text-red-600"}`}
          >
            {isCorrect
              ? "Correct! +1 coin"
              : `Incorrect. The correct word is: ${currentWord.word_text}`}
          </p>
          {!isCorrect && currentWord.english_text && currentWord.english_text !== currentWord.word_text && (
            <p className="text-sm text-gray-600 mt-1">
              ({currentWord.english_text})
            </p>
          )}
          {currentWord.audioWordUrl && (
            <button
              onClick={() => playAudio(currentWord.audioWordUrl)}
              className="mt-2 px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 inline-flex items-center"
            >
              <FiPlay className="mr-1" /> Listen
            </button>
          )}
          <button
            onClick={nextWord}
            className="mt-4 ml-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {singleWordMode
              ? "Try Again"
              : currentWordIndex === words.length - 1
                ? "Finish Test"
                : "Next Word"}
          </button>
        </div>
      )}
    </div>
  );
};

WordTest.propTypes = {
  wordList: PropTypes.shape({
    id: PropTypes.number.isRequired,
    language: PropTypes.string,
    languageName: PropTypes.string.isRequired,
    words: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        word_text: PropTypes.string.isRequired,
        sentence: PropTypes.string,
        audioWordUrl: PropTypes.string,
        audioSentenceUrl: PropTypes.string,
        english_text: PropTypes.string,
        imageUrl: PropTypes.string,
      }),
    ),
  }),
  singleWordMode: PropTypes.bool,
};

export default WordTest;
